// PetrolVibes — AI car keychain (upload → design → add to cart).
// Config comes from data-* attributes on #pvkc, and every customer-facing
// word from the JSON in #pvkcText (see snippets/pv-keychain.liquid), so the
// English, German and French stores share this exact file.
// Flow: photo → Turnstile + proof-of-work → keychain-generate → poll
// keychain-status → 1000x1000 design → quantity → keychain-add-to-cart →
// /cart/add.js. No email. After adding, the button splits into
// "Add to cart" | "Make another", and the trigger row always reopens the tool.
;(() => {
  const root = document.getElementById('pvkc')
  if (!root) return
  const cfg = root.dataset
  const SUPABASE_URL = cfg.supabaseUrl
  const ANON_KEY = cfg.anonKey
  const SITEKEY = cfg.sitekey
  const PRICE = cfg.price || ''
  const MAX_QTY = Math.max(1, parseInt(cfg.maxQty || '10', 10) || 10)
  // LAN test page only (test/index.html): replaces Turnstile, which refuses IP
  // addresses. Never set on the live store.
  const TEST_KEY = cfg.testKey || ''
  // Off the store (LAN test machine) there's no Shopify cart to add to.
  const ON_STORE = !/^(localhost|127\.0\.0\.1|192\.168\.\d+\.\d+|10\.\d+\.\d+\.\d+)$/.test(location.hostname)

  // Customer-facing text: English defaults, replaced by the snippet's language.
  const T = {
    verifySlow: 'Verification is taking longer than usual.',
    uploadFirst: 'Upload a photo first',
    verifyFailedLoad: "Verification didn't load — try again above",
    verifyingHuman: "Verifying you're human…",
    design: 'Design my keychain — free',
    tooLarge: 'Image too large (max 10 MB). Please choose a smaller photo.',
    verifyFailed: 'Verification failed. Please try again.',
    verifying: 'Verifying…',
    loading: ['Analysing your car…', 'Drawing your keychain…', 'Cutting it out…', 'Almost done…'],
    genericError: 'Something went wrong. Please try again.',
    slow: 'Taking longer than expected. Please try again in a moment.',
    inCartOne: '1 keychain in your cart — add another',
    inCartMany: '{n} keychains in your cart — add another',
    adding: 'Adding…',
    addFailed: 'Could not add your keychain to the cart. Please try again.',
    cartFailed: 'Your keychain was created, but adding it to the cart failed. Please try again.',
    addedOne: '✓ Added to your cart',
    addedMany: '✓ Added {n} to your cart',
    offStore: '✓ Product created (variant {id}). The cart only works on the store.',
    addToCart: 'Add to cart',
    continueShopping: 'Continue shopping',
  }
  try { Object.assign(T, JSON.parse(document.getElementById('pvkcText')?.textContent || '{}')) } catch (e) { console.error('pvkc: bad text JSON', e) }
  const fill = (text, values) => text.replace(/\{(\w+)\}/g, (m, k) => (k in values ? values[k] : m))

  const fnUrl = (name) => `${SUPABASE_URL}/functions/v1/${name}`
  const auth = { Authorization: `Bearer ${ANON_KEY}`, apikey: ANON_KEY }
  const $ = (id) => document.getElementById(id)

  const overlay = $('pvkcOverlay')
  const toastEl = $('pvkcToast')
  // A transformed ancestor (sticky headers, drawers…) would trap position:fixed;
  // living on <body> avoids whatever the theme wraps the section in.
  document.body.appendChild(overlay)
  document.body.appendChild(toastEl)

  const state = {
    file: null,
    jobId: null,
    imageUrl: null,
    qty: 1,
    addedTotal: 0,      // keychains added to the cart this visit
    busy: false,
    added: false,       // the current design is in the cart (buttons split)
    autoGenerate: false, // "Generate again" waits for a fresh Turnstile token
  }

  // ── Toast ────────────────────────────────────────────────────
  let toastTimer
  function toast(message, isError = false) {
    toastEl.textContent = message
    toastEl.classList.toggle('is-error', isError)
    toastEl.classList.add('is-shown')
    clearTimeout(toastTimer)
    toastTimer = setTimeout(() => toastEl.classList.remove('is-shown'), 4500)
  }

  // ── Open / close ─────────────────────────────────────────────
  function openTool() {
    overlay.hidden = false
    document.documentElement.classList.add('pvkc-lock')
    loadTurnstile()
    if (!$('pvkcViewUpload').hidden) startTurnstileWatchdog()
  }
  function closeTool() {
    overlay.hidden = true
    document.documentElement.classList.remove('pvkc-lock')
    clearTimeout(ts.timer)
  }
  $('pvkcTrigger').addEventListener('click', openTool)
  $('pvkcClose').addEventListener('click', closeTool)
  document.addEventListener('keydown', (e) => { if (e.key === 'Escape' && !overlay.hidden) closeTool() })

  // ── Views + steps ────────────────────────────────────────────
  function showView(name) {
    ;['Upload', 'Loading', 'Result'].forEach((v) => { $(`pvkcView${v}`).hidden = v !== name })
    const step = { Upload: 1, Loading: 2, Result: 3 }[name]
    ;[1, 2, 3].forEach((i) => {
      $(`pvkcStep${i}`).classList.toggle('is-active', i === step)
      $(`pvkcStep${i}`).classList.toggle('is-done', i < step)
    })
    if (name === 'Upload') startTurnstileWatchdog()
    else clearTimeout(ts.timer)
  }

  // ── Turnstile (skipped entirely in LAN test mode) ────────────
  const ts = { token: null, widgetId: null, failed: false, timer: null, loadBegun: false }
  const TS_SCRIPT = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit&onload=pvkcTurnstileOnload'
  const TS_TIMEOUT_MS = 20000
  const TS_RETRY_MS = [1500, 4000]

  function showTurnstileHelp(show) {
    $('pvkcTurnstileHelp').hidden = !show
    if (show) $('pvkcTurnstileHelpText').textContent = T.verifySlow
  }
  function startTurnstileWatchdog() {
    clearTimeout(ts.timer)
    if (TEST_KEY || ts.token || overlay.hidden) return
    ts.timer = setTimeout(function check() {
      if (ts.token) return
      if (document.visibilityState === 'hidden') { ts.timer = setTimeout(check, TS_TIMEOUT_MS); return }
      ts.failed = true
      showTurnstileHelp(true)
      updateGenerateButton()
    }, TS_TIMEOUT_MS)
  }
  // Loaded on first open, so product pages don't pull Cloudflare in for nothing.
  function loadTurnstile(attempt = 0) {
    if (TEST_KEY) { $('pvkcTurnstile').hidden = true; return }
    if (attempt === 0 && ts.loadBegun) return
    ts.loadBegun = true
    const script = document.createElement('script')
    script.src = TS_SCRIPT
    script.async = true
    script.onerror = () => {
      script.remove()
      if (window.turnstile) return
      if (attempt < TS_RETRY_MS.length) { setTimeout(() => loadTurnstile(attempt + 1), TS_RETRY_MS[attempt]); return }
      ts.failed = true
      showTurnstileHelp(true)
      updateGenerateButton()
    }
    document.head.appendChild(script)
  }
  window.pvkcTurnstileOnload = () => {
    if (ts.widgetId !== null) return
    ts.widgetId = window.turnstile.render('#pvkcTurnstile', {
      sitekey: SITEKEY,
      theme: 'light',
      size: 'flexible',
      callback: (token) => {
        ts.token = token
        ts.failed = false
        clearTimeout(ts.timer)
        showTurnstileHelp(false)
        updateGenerateButton()
        if (state.autoGenerate) { state.autoGenerate = false; generate() }
      },
      'error-callback': () => { ts.token = null; ts.failed = true; showTurnstileHelp(true); updateGenerateButton() },
      'expired-callback': () => { ts.token = null; updateGenerateButton() },
      'timeout-callback': () => { ts.token = null; updateGenerateButton() },
    })
  }
  function resetTurnstile() {
    ts.token = null
    if (window.turnstile && ts.widgetId !== null) window.turnstile.reset(ts.widgetId)
    updateGenerateButton()
  }
  $('pvkcTurnstileRetry').addEventListener('click', () => {
    ts.failed = false
    showTurnstileHelp(false)
    if (window.turnstile && ts.widgetId !== null) resetTurnstile()
    else { ts.loadBegun = false; loadTurnstile() }
    startTurnstileWatchdog()
    updateGenerateButton()
  })
  const verified = () => !!TEST_KEY || !!ts.token

  // ── Generate button ──────────────────────────────────────────
  function updateGenerateButton() {
    const btn = $('pvkcGenerate')
    if (state.busy) return
    if (!state.file) { btn.disabled = true; btn.textContent = T.uploadFirst; return }
    if (!verified() && ts.failed) { btn.disabled = true; btn.textContent = T.verifyFailedLoad; return }
    if (!verified()) { btn.disabled = true; btn.innerHTML = '<span class="pvkc-btn-spinner"></span>'; btn.append(T.verifyingHuman); return }
    btn.disabled = false
    btn.textContent = T.design
  }

  // ── Photo in ─────────────────────────────────────────────────
  const MAX_UPLOAD_DIMENSION = 2048
  const MAX_UPLOAD_BYTES = 10 * 1024 * 1024
  async function shrinkIfNeeded(file) {
    if (file.type === 'image/tiff' || /\.tiff?$/i.test(file.name)) return file
    try {
      const bitmap = await createImageBitmap(file)
      if (bitmap.width <= MAX_UPLOAD_DIMENSION && bitmap.height <= MAX_UPLOAD_DIMENSION) { bitmap.close(); return file }
      const scale = MAX_UPLOAD_DIMENSION / Math.max(bitmap.width, bitmap.height)
      const canvas = document.createElement('canvas')
      canvas.width = Math.round(bitmap.width * scale)
      canvas.height = Math.round(bitmap.height * scale)
      canvas.getContext('2d').drawImage(bitmap, 0, 0, canvas.width, canvas.height)
      bitmap.close()
      const blob = await new Promise((resolve) => canvas.toBlob(resolve, 'image/jpeg', 0.9))
      return blob ? new File([blob], file.name.replace(/\.\w+$/, '.jpg'), { type: 'image/jpeg' }) : file
    } catch (e) {
      console.error('pvkc: resize failed, uploading original', e)
      return file
    }
  }
  async function takePhoto(file) {
    if (!file || !file.type.startsWith('image/') && !/\.(tiff?|avif)$/i.test(file.name)) return
    const shrunk = await shrinkIfNeeded(file)
    if (shrunk.size > MAX_UPLOAD_BYTES) { toast(T.tooLarge, true); return }
    state.file = shrunk
    const reader = new FileReader()
    reader.onload = (ev) => { $('pvkcThumb').src = ev.target.result }
    reader.readAsDataURL(shrunk)
    $('pvkcDrop').hidden = true
    $('pvkcThumbWrap').hidden = false
    updateGenerateButton()
  }
  function clearPhoto() {
    state.file = null
    $('pvkcFile').value = ''
    $('pvkcThumb').removeAttribute('src')
    $('pvkcDrop').hidden = false
    $('pvkcThumbWrap').hidden = true
    updateGenerateButton()
  }
  $('pvkcFile').addEventListener('change', (e) => takePhoto(e.target.files[0]))
  $('pvkcRemove').addEventListener('click', clearPhoto)
  const drop = $('pvkcDrop')
  drop.addEventListener('dragover', (e) => { e.preventDefault(); drop.classList.add('is-dragging') })
  drop.addEventListener('dragleave', () => drop.classList.remove('is-dragging'))
  drop.addEventListener('drop', (e) => {
    e.preventDefault()
    drop.classList.remove('is-dragging')
    takePhoto(e.dataTransfer.files[0])
  })
  document.addEventListener('paste', (e) => {
    if (overlay.hidden || state.file || $('pvkcViewUpload').hidden) return
    const item = [...(e.clipboardData?.items || [])].find((i) => i.type.startsWith('image/'))
    if (item) takePhoto(item.getAsFile())
  })

  // ── SHA-256 (proof-of-work + fingerprint) ────────────────────
  // crypto.subtle only exists on HTTPS/localhost; this pure-JS version covers
  // plain-http LAN testing. On the live store crypto.subtle is used.
  function pvkcSha256(msg) {
    const K = [0x428a2f98, 0x71374491, 0xb5c0fbcf, 0xe9b5dba5, 0x3956c25b, 0x59f111f1, 0x923f82a4, 0xab1c5ed5,
      0xd807aa98, 0x12835b01, 0x243185be, 0x550c7dc3, 0x72be5d74, 0x80deb1fe, 0x9bdc06a7, 0xc19bf174,
      0xe49b69c1, 0xefbe4786, 0x0fc19dc6, 0x240ca1cc, 0x2de92c6f, 0x4a7484aa, 0x5cb0a9dc, 0x76f988da,
      0x983e5152, 0xa831c66d, 0xb00327c8, 0xbf597fc7, 0xc6e00bf3, 0xd5a79147, 0x06ca6351, 0x14292967,
      0x27b70a85, 0x2e1b2138, 0x4d2c6dfc, 0x53380d13, 0x650a7354, 0x766a0abb, 0x81c2c92e, 0x92722c85,
      0xa2bfe8a1, 0xa81a664b, 0xc24b8b70, 0xc76c51a3, 0xd192e819, 0xd6990624, 0xf40e3585, 0x106aa070,
      0x19a4c116, 0x1e376c08, 0x2748774c, 0x34b0bcb5, 0x391c0cb3, 0x4ed8aa4a, 0x5b9cca4f, 0x682e6ff3,
      0x748f82ee, 0x78a5636f, 0x84c87814, 0x8cc70208, 0x90befffa, 0xa4506ceb, 0xbef9a3f7, 0xc67178f2]
    const len = msg.length
    const padded = ((len + 9 + 63) >> 6) << 6
    const m = new Uint8Array(padded)
    m.set(msg); m[len] = 0x80
    const dv = new DataView(m.buffer)
    dv.setUint32(padded - 4, (len * 8) >>> 0)
    dv.setUint32(padded - 8, Math.floor((len * 8) / 0x100000000))
    let h0 = 0x6a09e667, h1 = 0xbb67ae85, h2 = 0x3c6ef372, h3 = 0xa54ff53a
    let h4 = 0x510e527f, h5 = 0x9b05688c, h6 = 0x1f83d9ab, h7 = 0x5be0cd19
    const w = new Uint32Array(64)
    for (let o = 0; o < padded; o += 64) {
      for (let i = 0; i < 16; i++) w[i] = dv.getUint32(o + i * 4)
      for (let i = 16; i < 64; i++) {
        const x = w[i - 15], y = w[i - 2]
        const s0 = ((x >>> 7) | (x << 25)) ^ ((x >>> 18) | (x << 14)) ^ (x >>> 3)
        const s1 = ((y >>> 17) | (y << 15)) ^ ((y >>> 19) | (y << 13)) ^ (y >>> 10)
        w[i] = (w[i - 16] + s0 + w[i - 7] + s1) | 0
      }
      let a = h0, b = h1, c = h2, d = h3, e = h4, f = h5, g = h6, h = h7
      for (let i = 0; i < 64; i++) {
        const S1 = ((e >>> 6) | (e << 26)) ^ ((e >>> 11) | (e << 21)) ^ ((e >>> 25) | (e << 7))
        const t1 = (h + S1 + ((e & f) ^ (~e & g)) + K[i] + w[i]) | 0
        const S0 = ((a >>> 2) | (a << 30)) ^ ((a >>> 13) | (a << 19)) ^ ((a >>> 22) | (a << 10))
        const t2 = (S0 + ((a & b) ^ (a & c) ^ (b & c))) | 0
        h = g; g = f; f = e; e = (d + t1) | 0; d = c; c = b; b = a; a = (t1 + t2) | 0
      }
      h0 = (h0 + a) | 0; h1 = (h1 + b) | 0; h2 = (h2 + c) | 0; h3 = (h3 + d) | 0
      h4 = (h4 + e) | 0; h5 = (h5 + f) | 0; h6 = (h6 + g) | 0; h7 = (h7 + h) | 0
    }
    const out = new Uint8Array(32), odv = new DataView(out.buffer)
    ;[h0, h1, h2, h3, h4, h5, h6, h7].forEach((v, i) => odv.setUint32(i * 4, v >>> 0))
    return out
  }
  async function pvkcDigest(bytes) {
    if (self.crypto && self.crypto.subtle) return new Uint8Array(await crypto.subtle.digest('SHA-256', bytes))
    return pvkcSha256(bytes)
  }

  // ── Proof-of-work (shared pow-challenge function) ────────────
  const POW_WORKER_SRC = pvkcSha256.toString() + '\n' + pvkcDigest.toString() + `
    function leadingZeroBits(bytes) {
      let count = 0
      for (const byte of bytes) {
        if (byte === 0) { count += 8; continue }
        for (let i = 7; i >= 0; i--) { if ((byte >> i) & 1) return count; count++ }
        return count
      }
      return count
    }
    self.onmessage = async (e) => {
      const { id, difficulty, expiresAt } = e.data
      const enc = new TextEncoder()
      for (let nonce = 0; ; nonce++) {
        if (leadingZeroBits(await pvkcDigest(enc.encode(id + ':' + expiresAt + ':' + difficulty + ':' + nonce))) >= difficulty) {
          self.postMessage({ nonce }); return
        }
      }
    }`
  function solvePow(id, difficulty, expiresAt) {
    return new Promise((resolve, reject) => {
      const worker = new Worker(URL.createObjectURL(new Blob([POW_WORKER_SRC], { type: 'application/javascript' })))
      worker.onmessage = (e) => { resolve(e.data.nonce); worker.terminate() }
      worker.onerror = (e) => { reject(e); worker.terminate() }
      worker.postMessage({ id, difficulty, expiresAt })
    })
  }
  async function deviceFingerprint() {
    const parts = []
    try {
      const canvas = document.createElement('canvas')
      canvas.width = 220; canvas.height = 40
      const ctx = canvas.getContext('2d')
      ctx.textBaseline = 'top'; ctx.font = '14px "Arial"'; ctx.fillStyle = '#f60'; ctx.fillRect(0, 0, 220, 40)
      ctx.fillStyle = '#069'; ctx.fillText('PVKC-fp 🚗 keychain 0123', 2, 2)
      parts.push(canvas.toDataURL())
    } catch { parts.push('') }
    try { parts.push(`${screen.width}x${screen.height}x${screen.colorDepth}`) } catch { parts.push('') }
    try { parts.push(String(navigator.hardwareConcurrency || '')) } catch { parts.push('') }
    try {
      const hash = await pvkcDigest(new TextEncoder().encode(parts.join('|')))
      return Array.from(hash).map((b) => b.toString(16).padStart(2, '0')).join('')
    } catch { return '' }
  }

  // ── Generate ─────────────────────────────────────────────────
  const LOADING_LABELS = T.loading
  async function generate() {
    if (!state.file || state.busy || !verified()) return
    state.busy = true
    const btn = $('pvkcGenerate')
    btn.disabled = true
    btn.innerHTML = '<span class="pvkc-btn-spinner"></span>'; btn.append(T.verifying)
    let pow, fp
    try {
      const [chRes, fingerprint] = await Promise.all([fetch(fnUrl('pow-challenge'), { headers: auth }), deviceFingerprint()])
      if (!chRes.ok) throw new Error('challenge request failed')
      const ch = await chRes.json()
      pow = { ...ch, nonce: await solvePow(ch.id, ch.difficulty, ch.expiresAt) }
      fp = fingerprint
    } catch (e) {
      state.busy = false
      updateGenerateButton()
      toast(T.verifyFailed, true)
      return
    }
    const form = new FormData()
    form.append('image', state.file)
    if (TEST_KEY) form.append('test_key', TEST_KEY)
    else form.append('turnstile_token', ts.token)
    form.append('pow_id', pow.id)
    form.append('pow_difficulty', String(pow.difficulty))
    form.append('pow_expires_at', String(pow.expiresAt))
    form.append('pow_signature', pow.signature)
    form.append('pow_nonce', String(pow.nonce))
    if (fp) form.append('device_fp', fp)

    showView('Loading')
    let li = 0
    $('pvkcLoadingLabel').textContent = LOADING_LABELS[0]
    const labelTimer = setInterval(() => { li++; $('pvkcLoadingLabel').textContent = LOADING_LABELS[li % LOADING_LABELS.length] }, 6000)
    try {
      const res = await fetch(fnUrl('keychain-generate'), { method: 'POST', headers: auth, body: form })
      const data = await res.json().catch(() => ({}))
      resetTurnstile() // tokens are single-use
      if (!res.ok) throw new Error(data.error || `Error ${res.status}`)
      const job = await pollJob(data.job_id)
      if (job.status === 'failed') throw new Error(job.error || T.genericError)
      state.jobId = data.job_id
      state.imageUrl = job.image_url
      showResult()
    } catch (e) {
      showView('Upload')
      toast(e.message || T.genericError, true)
    } finally {
      clearInterval(labelTimer)
      state.busy = false
      updateGenerateButton()
    }
  }
  async function pollJob(jobId) {
    const startedAt = Date.now()
    while (Date.now() - startedAt < 5 * 60 * 1000) {
      const res = await fetch(`${fnUrl('keychain-status')}?job_id=${encodeURIComponent(jobId)}`, { headers: auth })
      const data = await res.json().catch(() => ({}))
      if (!res.ok) throw new Error(data.error || `Error ${res.status}`)
      if (data.status === 'ready' || data.status === 'done' || data.status === 'failed') return data
      await new Promise((r) => setTimeout(r, 2500))
    }
    throw new Error(T.slow)
  }
  $('pvkcGenerate').addEventListener('click', generate)

  // ── Result: quantity + add to cart ───────────────────────────
  function setQty(n) {
    state.qty = Math.min(MAX_QTY, Math.max(1, parseInt(n, 10) || 1))
    $('pvkcQty').value = state.qty
    $('pvkcQtyMinus').disabled = state.qty <= 1
    $('pvkcQtyPlus').disabled = state.qty >= MAX_QTY
    $('pvkcPrice').textContent = state.qty > 1 && PRICE ? `${PRICE} × ${state.qty}` : PRICE
  }
  $('pvkcQtyMinus').addEventListener('click', () => setQty(state.qty - 1))
  $('pvkcQtyPlus').addEventListener('click', () => setQty(state.qty + 1))
  $('pvkcQty').addEventListener('change', (e) => setQty(e.target.value))

  function showResult() {
    $('pvkcArt').src = state.imageUrl
    setQty(1)
    $('pvkcStatus').textContent = ''
    setSplit(false)
    showView('Result')
  }
  // After the first add: "Make another" | "Continue shopping" (closes the
  // tool). The quantity row hides then, since this design is in the cart.
  function setSplit(on) {
    state.added = on
    $('pvkcActions').classList.toggle('is-split', on)
    $('pvkcAnother').hidden = !on
    $('pvkcAdd').textContent = on ? T.continueShopping : T.addToCart
    document.querySelector('.pvkc-buy-row').hidden = on
  }
  function markAdded(count) {
    state.addedTotal += count
    const trigger = $('pvkcTrigger')
    trigger.classList.add('is-added')
    $('pvkcTriggerText').textContent = state.addedTotal === 1
      ? T.inCartOne
      : fill(T.inCartMany, { n: state.addedTotal })
    setSplit(true)
  }

  async function addToCart() {
    if (state.added) { closeTool(); return } // "Continue shopping"
    if (!state.jobId || state.busy) return
    state.busy = true
    const btn = $('pvkcAdd')
    btn.disabled = true
    btn.innerHTML = '<span class="pvkc-btn-spinner"></span>'; btn.append(T.adding)
    $('pvkcStatus').textContent = ''
    const qty = state.qty
    try {
      const res = await fetch(fnUrl('keychain-add-to-cart'), {
        method: 'POST',
        headers: { ...auth, 'Content-Type': 'application/json' },
        body: JSON.stringify({ job_id: state.jobId }),
      })
      const data = await res.json().catch(() => ({}))
      if (!res.ok || !data.variant_id) throw new Error(data.error || T.addFailed)
      if (ON_STORE) {
        const cartRes = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items: [{ id: Number(data.variant_id), quantity: qty }] }),
        })
        if (!cartRes.ok) throw new Error(T.cartFailed)
        // Motion theme: cart:build rebuilds the cart drawer and the header cart
        // count without opening the drawer over the tool.
        document.dispatchEvent(new CustomEvent('cart:build'))
        $('pvkcStatus').textContent = qty === 1 ? T.addedOne : fill(T.addedMany, { n: qty })
      } else {
        $('pvkcStatus').textContent = fill(T.offStore, { id: data.variant_id })
      }
      markAdded(qty)
    } catch (e) {
      toast(e.message || T.genericError, true)
    } finally {
      state.busy = false
      btn.disabled = false
      btn.textContent = state.added ? T.continueShopping : T.addToCart
    }
  }
  $('pvkcAdd').addEventListener('click', addToCart)

  // "Make another": fresh photo, fresh design; what's in the cart stays.
  $('pvkcAnother').addEventListener('click', () => {
    state.jobId = null
    state.imageUrl = null
    clearPhoto()
    setSplit(false)
    showView('Upload')
  })
  // "Generate again": same photo, new design.
  $('pvkcRetry').addEventListener('click', () => {
    state.jobId = null
    showView('Upload')
    if (verified()) generate()
    else state.autoGenerate = true
  })

  setQty(1)
  updateGenerateButton()
})()
